import {useQuery} from '@tanstack/react-query'
import {useSearchParams, Link} from 'react-router-dom'
import {Fragment, useCallback, useMemo, useState, useEffect} from 'react'
import {Shield, Clock, ChevronDown, ChevronRight, ListTree} from 'lucide-react'
import {routes} from '@/lib/routes'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {StatusText} from '@/components/shared/StatusText'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatDate, formatDateShort, isMaxInstant} from '@/lib/date-utils'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {CardContent} from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {MyAccessTabId, MyAccessTestIds} from '@/testids/MyAccessTestIds'
import {AccessTimeline, type TimelineEntry} from '@/components/identity/AccessTimeline'
import type {FunctionGroupTimelineEvent} from '@/types/timeline'
import {AssignmentStatus, AssignmentEventType, AssignmentSourceType, AssignmentEventTypeBadge} from '@/types/assignment'
import {
    assignmentSourceTypeIcon,
    useAssignmentEventTypeLabel,
    useAssignmentSourceTypeLabel,
    useFunctionGroupAssignmentStatusLabel,
} from '@/features/identity/labels'
import {FunctionGroupAssignmentStatusBadge} from '@/types/function-group'
import {CertificationDecisionResponseFields} from '@/types/certification'
import {EmptyState} from '@/components/shared/EmptyState'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {SmallText} from '@/components/shared/SmallText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {IconText} from '@/components/shared/IconText'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {NewRequestButtons} from '@/features/my/NewRequestButtons'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {mergeQueryState} from '@/lib/query-utils'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {
    EffectiveAccessControllerMyEffectiveAccessEndpoint,
    MyAccessControllerMyAccessEndpoint,
    MyAccessControllerMyFunctionGroupAccessEndpoint,
} from '@/types/endpoint-contracts'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'

function formatExpiresAt(dateStr: string | null | undefined, i18n: ReturnType<typeof useI18n>, useShortFormat = false): string {
    if (!dateStr) return '—'
    if (isMaxInstant(dateStr)) {
        return String(i18n.common.permanent)
    }
    return useShortFormat ? formatDateShort(dateStr) : formatDate(dateStr)
}

export function MyAccess() {
    const services = useServices()
    const i18n = useI18n()
    const fgStatusLabel = useFunctionGroupAssignmentStatusLabel()
    const sourceTypeLabel = useAssignmentSourceTypeLabel()
    const eventTypeLabel = useAssignmentEventTypeLabel()
    const {user} = useAuth()
    const {assignmentStatuses, enforcementTypes} = useAppConfig()
    const [searchParams, setSearchParams] = useSearchParams()
    const assignmentIdParam = searchParams.get(CertificationDecisionResponseFields.assignmentId)

    const [expandedAssignmentId, setExpandedAssignmentId] = useState<string | null>(null)
    const [expandedFgAssignmentId, setExpandedFgAssignmentId] = useState<string | null>(null)
    const tabParam = searchParams.get('tab') ?? MyAccessTabId.AccessProfiles
    const [activeTab, setActiveTabState] = useState<string>(tabParam)
    const setActiveTab = useCallback((tab: string) => {
        setActiveTabState(tab)
        setSearchParams(prev => {
            const next = new URLSearchParams(prev)
            next.set('tab', tab)
            return next
        })
    }, [setSearchParams])

    const {
        page: apPage,
        pageSize: apPageSize,
        queryString: apQueryString,
        setPage: setApPage,
        setPageSize: setApPageSize,
        pageSizeOptions: apPageSizeOptions,
        searchAndFilters: apSearchAndFilters,
        setSearchAndFilters: setApSearchAndFilters,
    } = useServerPagination({
        endpoint: MyAccessControllerMyAccessEndpoint,
        defaultSortBy: '',
    })

    const {data: apData, isLoading, error} = useQuery({
        queryKey: ['my-access', apQueryString],
        queryFn: () => services.myAccess.assignments(Object.fromEntries(new URLSearchParams(apQueryString))),
        enabled: !!user,
    })
    const assignments = useMemo(() => apData?.content ?? [], [apData])
    const apTotalItems = apData?.totalElements ?? 0
    const apTotalPages = apData?.totalPages ?? 0
    const apHasActiveFilters = useMemo(() => {
        return !!apSearchAndFilters.q
            || Object.keys(apSearchAndFilters.filters).length > 0
            || Object.keys(apSearchAndFilters.dynamicAttributes).length > 0
    }, [apSearchAndFilters])

    const {data: timelineData, isLoading: timelineLoading} = useQuery({
        queryKey: ['my-access-timeline'],

        queryFn: () => services.myAccess.timeline({size: 100}),
        enabled: !!user,
    })

    const {
        page: fgPage,
        pageSize: fgPageSize,
        queryString: fgQueryString,
        setPage: setFgPage,
        setPageSize: setFgPageSize,
        pageSizeOptions: fgPageSizeOptions,
        searchAndFilters: fgSearchAndFilters,
        setSearchAndFilters: setFgSearchAndFilters,
    } = useServerPagination({
        endpoint: MyAccessControllerMyFunctionGroupAccessEndpoint,
        defaultSortBy: '',
    })

    const {data: fgData, isLoading: fgAssignmentsLoading, error: fgAssignmentsError} = useQuery({
        queryKey: ['my-function-group-access', fgQueryString],
        queryFn: () => services.myAccess.functionGroupAssignments(Object.fromEntries(new URLSearchParams(fgQueryString))),
        enabled: !!user,
    })
    const fgAssignments = fgData?.content ?? []
    const fgTotalItems = fgData?.totalElements ?? 0
    const fgTotalPages = fgData?.totalPages ?? 0
    const fgHasActiveFilters = useMemo(() => {
        return !!fgSearchAndFilters.q
            || Object.keys(fgSearchAndFilters.filters).length > 0
            || Object.keys(fgSearchAndFilters.dynamicAttributes).length > 0
    }, [fgSearchAndFilters])

    const {data: fgTimelineData, isLoading: fgTimelineLoading} = useQuery({
        queryKey: ['my-function-group-access-timeline'],
        queryFn: () => services.myAccess.functionGroupTimeline({size: 100}),
        enabled: !!user,
    })

    const fgTimelineEntries: TimelineEntry[] = useMemo(
        () => (fgTimelineData?.content ?? []).map((e: FunctionGroupTimelineEvent): TimelineEntry => ({
            id: e.id,
            eventType: e.eventType,
            oldExpiresAt: e.oldExpiresAt,
            newExpiresAt: e.newExpiresAt,
            performedBy: e.performedBy,
            comment: e.comment,
            createdAt: e.createdAt,
            sourceId: e.sourceId,
            sourceType: e.sourceType,
            sourceName: null,
            title: e.functionGroupName,
            subtitle: null,
        })),
        [fgTimelineData],
    )

    useEffect(() => {
        if (assignmentIdParam && assignments.length > 0) {
            const match = assignments.find(a => a.id === assignmentIdParam)
            if (match) {
                setExpandedAssignmentId(assignmentIdParam)
            }
        }
    }, [assignmentIdParam, assignments])

    const toggleAssignment = (id: string) => {
        setExpandedAssignmentId(prev => prev === id ? null : id)
    }

    const toggleFgAssignment = (id: string) => {
        setExpandedFgAssignmentId(prev => prev === id ? null : id)
    }

    const {isLoading: mergedLoading, error: mergedError} = mergeQueryState([
        {isLoading, error},
        {isLoading: fgAssignmentsLoading, error: fgAssignmentsError},
    ])

    const {
        page: eaPage,
        pageSize: eaPageSize,
        queryString: eaQueryString,
        setPage: setEaPage,
        setPageSize: setEaPageSize,
        pageSizeOptions: eaPageSizeOptions,
        searchAndFilters: eaSearchAndFilters,
        setSearchAndFilters: setEaSearchAndFilters,
    } = useServerPagination({
        endpoint: EffectiveAccessControllerMyEffectiveAccessEndpoint,
        defaultSortBy: EffectiveAccessControllerMyEffectiveAccessEndpoint.sort.VALID_FROM,
        defaultSortDir: SortDir.DESC,
    })

    const {data: eaData, isLoading: eaLoading} = useQuery({
        queryKey: ['my-effective-access', eaQueryString],
        queryFn: () => services.myAccess.effectiveAccess(Object.fromEntries(new URLSearchParams(eaQueryString))),
        enabled: !!user,
    })

    const eaTotalItems = eaData?.totalElements ?? 0
    const eaTotalPages = eaData?.totalPages ?? 0

    const tabs: TabDefinition[] = [
        {
            key: MyAccessTabId.AccessProfiles,
            label: String(i18n.myAccess.accessProfilesTab),
            count: apTotalItems > 0 ? apTotalItems : undefined,
        },
        {
            key: MyAccessTabId.FunctionGroups,
            label: String(i18n.myAccess.functionGroupsTab),
            count: fgTotalItems > 0 ? fgTotalItems : undefined,
        },
        {
            key: MyAccessTabId.EffectiveAccess,
            label: String(i18n.myAccess.effectiveAccessTab),
            count: eaTotalItems > 0 ? eaTotalItems : undefined,
        },
    ]

    const eaHasActiveFilters = useMemo(() => {
        return !!eaSearchAndFilters.q
            || Object.keys(eaSearchAndFilters.filters).length > 0
            || Object.keys(eaSearchAndFilters.dynamicAttributes).length > 0
    }, [eaSearchAndFilters])

    const renderAccessProfilesContent = () => (
        <TabPanelContent
            filters={
                <UnifiedSearchBar
                    contract={MyAccessControllerMyAccessEndpoint}
                    value={apSearchAndFilters}
                    onChange={setApSearchAndFilters}
                    placeholder={i18n.myAccess.accessProfilesSearchPlaceholder as string}
                    testIdNamespace="my-access-profiles"
                />
            }
        >
            <Tabs defaultValue={MyAccessTabId.Assignments}>
                <TabsList className="ml-6">
                    <TabsTrigger value={MyAccessTabId.Assignments} data-testid={MyAccessTestIds.tab(MyAccessTabId.Assignments)}>{i18n.myAccess.byAssignment}</TabsTrigger>
                    <TabsTrigger value={MyAccessTabId.Timeline} data-testid={MyAccessTestIds.tab(MyAccessTabId.Timeline)}>{i18n.myAccess.timeline}</TabsTrigger>
                </TabsList>

                <TabsContent value="assignments">
                    <CardContent>
                        {assignments.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-8" data-testid="col-expand"></TableHead>
                                        <TableHead data-testid="col-access-profiles">{i18n.identities.accessProfiles}</TableHead>
                                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                        <TableHead data-testid="col-sources">{i18n.identities.sources}</TableHead>
                                        <TableHead data-testid="col-expires-at">{i18n.identities.expiresAt}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {assignments.map((assignment) => (
                                        <Fragment key={assignment.id}>
                                            <TableRow
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => toggleAssignment(assignment.id)}
                                                data-testid="my-access-row"
                                            >
                                                <TableCell className="w-8">
                                                    {expandedAssignmentId === assignment.id ? (
                                                        <Icon icon={ChevronDown} muted/>
                                                    ) : (
                                                        <Icon icon={ChevronRight} muted/>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <TextLink
                                                            to={routes.accessGovernance.profiles.detail(assignment.accessProfileId)}
                                                            stopPropagation
                                                            testId={`link-access-profile-${assignment.id}`}
                                                            variant="bold"
                                                        >
                                                            {assignment.accessProfileName}
                                                        </TextLink>
                                                    </div>
                                                    <MutedText size="sm">
                                                        {assignment.systemName} / {assignment.resourceName}
                                                    </MutedText>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={
                                                        assignment.status === AssignmentStatus.ACTIVE ? BadgeVariant.SUCCESS :
                                                            assignment.status === AssignmentStatus.PENDING ? BadgeVariant.SECONDARY :
                                                                assignment.status === AssignmentStatus.EXPIRED ? BadgeVariant.OUTLINE :
                                                                    BadgeVariant.DESTRUCTIVE
                                                    } data-testid={`my-access-status-${assignment.status}`}>
                                                        {labelOf(assignmentStatuses, assignment.status)}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <InlineRow wrap gap="xs">
                                                        {assignment.sources.filter(s => s.active).length === 0 ? (
                                                            <MutedText size="sm">{i18n.identities.activeSources({count: 0})}</MutedText>
                                                        ) : (
                                                            assignment.sources.filter(s => s.active).map((source) => {
                                                                const SourceIcon = assignmentSourceTypeIcon(source.sourceType)
                                                                return (
                                                                    <Badge
                                                                        key={source.id}
                                                                        variant="outline"
                                                                        className="gap-1 text-xs"
                                                                    >
                                                                        <SourceIcon className="h-3 w-3"/>
                                                                        {source.sourceType === AssignmentSourceType.RULE ? (
                                                                            <Link
                                                                                to={routes.accessGovernance.rules.detail(source.ruleId!)}
                                                                                className="hover:underline"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                data-testid={`link-rule-${source.ruleId}`}
                                                                            >
                                                                                {source.ruleName}
                                                                            </Link>
                                                                        ) : (
                                                                            sourceTypeLabel(source.sourceType)
                                                                        )}
                                                                    </Badge>
                                                                )
                                                            })
                                                        )}
                                                    </InlineRow>
                                                </TableCell>
                                                <TableCell>
                                                    {assignment.permanent ? (
                                                        <MutedText>{i18n.common.permanent}</MutedText>
                                                    ) : (
                                                        <IconText icon={Clock} size="sm">
                                                            {formatExpiresAt(assignment.expiresAt, i18n)}
                                                        </IconText>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {expandedAssignmentId === assignment.id && (
                                                <TableRow key={`${assignment.id}-events`}>
                                                    <TableCell colSpan={5} className="bg-muted/30">
                                                        {assignment.events && assignment.events.length > 0 ? (
                                                            <Stack gap="xs" leftBorder>
                                                                {assignment.events.map((event) => {
                                                                    const badge = AssignmentEventTypeBadge.of(event.eventType)
                                                                    const SourceIcon = event.sourceType ? assignmentSourceTypeIcon(event.sourceType) : null
                                                                    return (
                                                                        <InlineRow key={event.id} gap="sm">
                                                                            <SmallText className="whitespace-nowrap">
                                                                                {formatDate(event.createdAt)}
                                                                            </SmallText>
                                                                            <Badge
                                                                                variant={badge?.variant ?? BadgeVariant.OUTLINE}
                                                                                data-testid={`event-type-${event.eventType}`}
                                                                            >
                                                                                {eventTypeLabel(event.eventType)}
                                                                            </Badge>
                                                                            <MutedText size="sm" className="flex-1">
                                                                                {event.eventType === AssignmentEventType.CREATED && event.newExpiresAt && (
                                                                                    <>{i18n.identities.expiresAt}: {formatExpiresAt(event.newExpiresAt, i18n)}</>
                                                                                )}
                                                                                {(event.eventType === AssignmentEventType.EXTENDED || event.eventType === AssignmentEventType.SHORTENED) && (
                                                                                    <>
                                                                                        {formatExpiresAt(event.oldExpiresAt, i18n, true)}
                                                                                        {' \u2192 '}
                                                                                        {formatExpiresAt(event.newExpiresAt, i18n, true)}
                                                                                    </>
                                                                                )}
                                                                                {(event.eventType === AssignmentEventType.SOURCE_ADDED || event.eventType === AssignmentEventType.SOURCE_REMOVED || event.eventType === AssignmentEventType.SOURCE_RESTORED) && event.sourceName && SourceIcon && (
                                                                                    <>
                                                                                        <Icon icon={SourceIcon} size="xs" className="inline mr-1"/>
                                                                                        {event.sourceName}
                                                                                    </>
                                                                                )}
                                                                                {event.comment && <SmallText className="ml-2 italic">"{event.comment}"</SmallText>}
                                                                            </MutedText>
                                                                        </InlineRow>
                                                                    )
                                                                })}
                                                            </Stack>
                                                        ) : (
                                                            <MutedText size="sm">{i18n.myAccess.noHistoryEvents}</MutedText>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            apHasActiveFilters ? (
                                <EmptyState
                                    icon={Shield}
                                    title={String(i18n.myAccess.noAssignmentsMatch)}
                                />
                            ) : (
                                <EmptyState
                                    icon={Shield}
                                    title={String(i18n.myAccess.noAssignments)}
                                />
                            )
                        )}
                        {apTotalItems > 0 && (
                            <Pagination
                                page={apPage}
                                pageSize={apPageSize}
                                totalItems={apTotalItems}
                                totalPages={apTotalPages}
                                onPageChange={setApPage}
                                onPageSizeChange={setApPageSize}
                                pageSizeOptions={apPageSizeOptions}
                            />
                        )}
                    </CardContent>
                </TabsContent>

                <TabsContent value="timeline">
                    <CardContent>
                        <AccessTimeline
                            events={timelineData?.content ?? []}
                            isLoading={timelineLoading}
                        />
                    </CardContent>
                </TabsContent>
            </Tabs>
        </TabPanelContent>
    )

    const renderFunctionGroupsContent = () => (
        <TabPanelContent
            filters={
                <UnifiedSearchBar
                    contract={MyAccessControllerMyFunctionGroupAccessEndpoint}
                    value={fgSearchAndFilters}
                    onChange={setFgSearchAndFilters}
                    placeholder={i18n.myAccess.functionGroupsSearchPlaceholder as string}
                    testIdNamespace="my-function-groups"
                />
            }
        >
            <Tabs defaultValue={MyAccessTabId.FunctionGroupAssignments}>
                <TabsList className="ml-6">
                    <TabsTrigger
                        value={MyAccessTabId.FunctionGroupAssignments}
                        data-testid={MyAccessTestIds.tab(MyAccessTabId.FunctionGroupAssignments)}
                    >{i18n.myAccess.byAssignment}</TabsTrigger>
                    <TabsTrigger
                        value={MyAccessTabId.FunctionGroupTimeline}
                        data-testid={MyAccessTestIds.tab(MyAccessTabId.FunctionGroupTimeline)}
                    >{i18n.myAccess.timeline}</TabsTrigger>
                </TabsList>

                <TabsContent value={MyAccessTabId.FunctionGroupAssignments}>
            <CardContent>
                {fgAssignments.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8" data-testid="col-expand"></TableHead>
                                <TableHead data-testid="col-fg-name">{i18n.myAccess.fgColumnName}</TableHead>
                                <TableHead data-testid="col-fg-status">{i18n.myAccess.fgColumnStatus}</TableHead>
                                <TableHead data-testid="col-fg-sources">{i18n.myAccess.fgColumnSources}</TableHead>
                                <TableHead data-testid="col-fg-expires-at">{i18n.myAccess.fgColumnExpiresAt}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {fgAssignments.map((fgAssignment) => (
                                <Fragment key={fgAssignment.id}>
                                    <TableRow
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => toggleFgAssignment(fgAssignment.id)}
                                        data-testid="my-fg-assignment-row"
                                    >
                                        <TableCell className="w-8">
                                            {expandedFgAssignmentId === fgAssignment.id ? (
                                                <Icon icon={ChevronDown} muted/>
                                            ) : (
                                                <Icon icon={ChevronRight} muted/>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <TextLink
                                                to={routes.accessGovernance.functionGroups.detail(fgAssignment.functionGroupId)}
                                                stopPropagation
                                                testId={`link-function-group-${fgAssignment.id}`}
                                                variant="bold"
                                            >
                                                <span data-testid={`fg-name-${fgAssignment.id}`}>{fgAssignment.functionGroupName}</span>
                                            </TextLink>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const statusBadge = FunctionGroupAssignmentStatusBadge.of(fgAssignment.status)
                                                const StatusIcon = statusBadge?.icon
                                                return (
                                                    <Badge
                                                        variant={statusBadge?.variant ?? BadgeVariant.OUTLINE}
                                                        className="gap-1"
                                                        data-testid={`my-fg-status-${fgAssignment.status}`}
                                                    >
                                                        {StatusIcon && <StatusIcon className="h-3 w-3" aria-hidden="true"/>}
                                                        {fgStatusLabel(fgAssignment.status)}
                                                    </Badge>
                                                )
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <InlineRow wrap gap="xs">
                                                {fgAssignment.sources.filter(s => !s.removedAt).length === 0 ? (
                                                    <MutedText size="sm">{i18n.identities.activeSources({count: 0})}</MutedText>
                                                ) : (
                                                    fgAssignment.sources.filter(s => !s.removedAt).map((source) => {
                                                        const SourceIcon = assignmentSourceTypeIcon(source.sourceType)
                                                        return (
                                                            <Badge
                                                                key={source.id}
                                                                variant="outline"
                                                                className="gap-1 text-xs"
                                                            >
                                                                <SourceIcon className="h-3 w-3"/>
                                                                {source.sourceType === AssignmentSourceType.RULE && source.ruleId ? (
                                                                    <Link
                                                                        to={routes.accessGovernance.rules.detail(source.ruleId)}
                                                                        className="hover:underline"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        data-testid={`link-fg-rule-${source.ruleId}`}
                                                                    >
                                                                        {sourceTypeLabel(source.sourceType)}
                                                                    </Link>
                                                                ) : (
                                                                    sourceTypeLabel(source.sourceType)
                                                                )}
                                                            </Badge>
                                                        )
                                                    })
                                                )}
                                            </InlineRow>
                                        </TableCell>
                                        <TableCell>
                                            {fgAssignment.permanent ? (
                                                <MutedText>{i18n.common.permanent}</MutedText>
                                            ) : (
                                                <IconText icon={Clock} size="sm">
                                                    {formatExpiresAt(fgAssignment.expiresAt, i18n)}
                                                </IconText>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {expandedFgAssignmentId === fgAssignment.id && (
                                        <TableRow key={`${fgAssignment.id}-events`} data-testid={`fg-history-${fgAssignment.id}`}>
                                            <TableCell colSpan={5} className="bg-muted/30">
                                                {fgAssignment.events && fgAssignment.events.length > 0 ? (
                                                    <Stack gap="xs" leftBorder>
                                                        {fgAssignment.events.map((event) => {
                                                            const badge = AssignmentEventTypeBadge.of(event.eventType)
                                                            return (
                                                                <InlineRow key={event.id} gap="sm">
                                                                    <SmallText className="whitespace-nowrap">
                                                                        {formatDate(event.createdAt)}
                                                                    </SmallText>
                                                                    <Badge
                                                                        variant={badge?.variant ?? BadgeVariant.OUTLINE}
                                                                        data-testid={`event-type-${event.eventType}`}
                                                                    >
                                                                        {eventTypeLabel(event.eventType)}
                                                                    </Badge>
                                                                    <MutedText size="sm" className="flex-1">
                                                                        {event.eventType === AssignmentEventType.CREATED && event.newExpiresAt && (
                                                                            <>{i18n.identities.expiresAt}: {formatExpiresAt(event.newExpiresAt, i18n)}</>
                                                                        )}
                                                                        {(event.eventType === AssignmentEventType.EXTENDED || event.eventType === AssignmentEventType.SHORTENED) && (
                                                                            <>
                                                                                {formatExpiresAt(event.oldExpiresAt, i18n, true)}
                                                                                {' \u2192 '}
                                                                                {formatExpiresAt(event.newExpiresAt, i18n, true)}
                                                                            </>
                                                                        )}
                                                                        {event.comment && <SmallText className="ml-2 italic">"{event.comment}"</SmallText>}
                                                                    </MutedText>
                                                                </InlineRow>
                                                            )
                                                        })}
                                                    </Stack>
                                                ) : (
                                                    <MutedText size="sm">{i18n.myAccess.noHistoryEvents}</MutedText>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </Fragment>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div data-testid="my-fg-empty">
                        {fgHasActiveFilters ? (
                            <EmptyState
                                icon={ListTree}
                                title={String(i18n.myAccess.noFgAssignmentsMatch)}
                            />
                        ) : (
                            <EmptyState
                                icon={ListTree}
                                title={String(i18n.myAccess.noFgAssignments)}
                            />
                        )}
                    </div>
                )}
                {fgTotalItems > 0 && (
                    <Pagination
                        page={fgPage}
                        pageSize={fgPageSize}
                        totalItems={fgTotalItems}
                        totalPages={fgTotalPages}
                        onPageChange={setFgPage}
                        onPageSizeChange={setFgPageSize}
                        pageSizeOptions={fgPageSizeOptions}
                    />
                )}
            </CardContent>
                </TabsContent>

                <TabsContent value={MyAccessTabId.FunctionGroupTimeline}>
                    <CardContent>
                        <AccessTimeline
                            events={fgTimelineEntries}
                            isLoading={fgTimelineLoading}
                        />
                    </CardContent>
                </TabsContent>
            </Tabs>
        </TabPanelContent>
    )

    const renderEffectiveAccessContent = () => (
        <TabPanelContent
            filters={
                <UnifiedSearchBar
                    contract={EffectiveAccessControllerMyEffectiveAccessEndpoint}
                    value={eaSearchAndFilters}
                    onChange={setEaSearchAndFilters}
                    placeholder={i18n.myAccess.effectiveAccessSearchPlaceholder as string}
                    testIdNamespace="my-effective-access"
                />
            }
        >
            <CardContent>
                <StatusText
                    tone="info"
                    size="xs"
                    muted
                    data-testid="my-effective-access-scope-hint"
                >
                    {i18n.myAccess.effectiveAccessScopeHint}
                </StatusText>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead data-testid="col-ea-system">{i18n.myAccess.eaColumnSystem}</TableHead>
                            <TableHead data-testid="col-ea-enforcement-object">{i18n.myAccess.eaColumnEnforcementObject}</TableHead>
                            <TableHead data-testid="col-ea-resource">{i18n.myAccess.eaColumnResource}</TableHead>
                            <TableHead data-testid="col-ea-operation">{i18n.myAccess.eaColumnOperation}</TableHead>
                            <TableHead data-testid="col-ea-valid-from">{i18n.myAccess.eaColumnValidFrom}</TableHead>
                            <TableHead data-testid="col-ea-valid-to">{i18n.myAccess.eaColumnValidTo}</TableHead>
                            <TableHead data-testid="col-ea-sources-count">{i18n.myAccess.eaColumnSourcesCount}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {eaData?.content.map((grant) => (
                            <TableRow key={grant.accessId} data-testid="my-effective-access-row">
                                <TableCell>
                                    <EntityLinkChip
                                        value={grant.targetSystem}
                                        testId={`link-ea-system-${grant.accessId}`}
                                        showBadge={false}
                                    />
                                    <MutedText size="sm" block>{grant.targetSystem.subType ?? ''}</MutedText>
                                </TableCell>
                                <TableCell>
                                    <EntityLinkChip
                                        value={grant.enforcementObject}
                                        testId={`link-ea-enforcement-object-${grant.accessId}`}
                                        showBadge={false}
                                    />
                                    <MutedText size="sm" block>{labelOf(enforcementTypes, grant.enforcementObject.subType ?? '')}</MutedText>
                                </TableCell>
                                <TableCell>
                                    {grant.resource ? (
                                        <EntityLinkChip
                                            value={grant.resource}
                                            testId={`link-ea-resource-${grant.accessId}`}
                                        />
                                    ) : (
                                        <MutedText>{'\u2014'}</MutedText>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge variant={BadgeVariant.OUTLINE}>{grant.operation}</Badge>
                                </TableCell>
                                <TableCell>
                                    <MutedText size="sm">{formatDate(grant.validFrom)}</MutedText>
                                </TableCell>
                                <TableCell>
                                    {grant.permanent || !grant.validTo ? (
                                        <MutedText>{i18n.common.permanent}</MutedText>
                                    ) : (
                                        <IconText icon={Clock} size="sm">
                                            {formatExpiresAt(grant.validTo, i18n)}
                                        </IconText>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Badge variant={BadgeVariant.SECONDARY}>{grant.sources.length}</Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!eaLoading && eaData?.content.length === 0 && (
                            <TableEmptyState
                                colSpan={7}
                                hasFilters={eaHasActiveFilters}
                                matchMessage={String(i18n.myAccess.noEffectiveAccessMatch)}
                                emptyMessage={String(i18n.myAccess.noEffectiveAccess)}
                            />
                        )}
                    </TableBody>
                </Table>
                {eaTotalItems > 0 && (
                    <Pagination
                        page={eaPage}
                        pageSize={eaPageSize}
                        totalItems={eaTotalItems}
                        totalPages={eaTotalPages}
                        onPageChange={setEaPage}
                        onPageSizeChange={setEaPageSize}
                        pageSizeOptions={eaPageSizeOptions}
                    />
                )}
            </CardContent>
        </TabPanelContent>
    )

    const activeTabLabel = tabs.find(t => t.key === activeTab)?.label ?? ''
    const pageTitle = `${String(i18n.myAccess.title)}: ${activeTabLabel}`
    const tabDescriptions: Record<string, string> = {
        [MyAccessTabId.AccessProfiles]: String(i18n.myAccess.accessProfilesTabSection),
        [MyAccessTabId.FunctionGroups]: String(i18n.myAccess.functionGroupsTabSection),
        [MyAccessTabId.EffectiveAccess]: String(i18n.myAccess.effectiveAccessTabSection),
    }
    const pageDescription = tabDescriptions[activeTab] ?? String(i18n.myAccess.subtitle)

    return (
        <PageLayout
            variant="tabbed-list"
            icon={CONCEPT_ICON.account}
            title={pageTitle}
            description={pageDescription}
            actions={<NewRequestButtons/>}
            loading={mergedLoading}
            error={mergedError}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            data-testid="my-access-outer-tabs"
        >
            {activeTab === MyAccessTabId.AccessProfiles && renderAccessProfilesContent()}
            {activeTab === MyAccessTabId.FunctionGroups && renderFunctionGroupsContent()}
            {activeTab === MyAccessTabId.EffectiveAccess && renderEffectiveAccessContent()}
        </PageLayout>
    )
}
